import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles, Typography, OutlinedInput } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import OptimizedTooltip from "../common/OptimizedTooltip";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500,
    padding: theme.spacing(0.55, 1),
    maxWidth: 243,
    "&:hover": {
      borderRadius: 4,
      backgroundColor: "#F4F4F4"
    }
  },
  input: {
    padding: theme.spacing(0.5, 1),
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  }
}));

const HeaderTitle = props => {
  const {
    projectDate,
    projectName,
    updateProject,
    openDate,
    isNotAllowedToEdit
  } = props;

  // General
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  // Custom
  const productTour = useProductTour();

  // States
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Variables
  const isProductTourPartProjectInitializeStepSetTitle =
    productTour.isTourStepId(
      PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_SET_TITLE
    );
  const isProductTourPartProjectInitializeStepBrief = productTour.isTourStepId(
    PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BRIEF
  );

  // Refs
  const inputRef = useRef(null);

  // Callbacks
  const onTitleClick = () => {
    if (productTour.hasActiveTourCurrently()) {
      return;
    }
    if (!editMode) {
      setEditMode(true);
    }
  };

  const onInputBlur = event => {
    const newName = event.target.value;
    if (newName === "") {
      enqueueSnackbar("Project title cannot be empty.");
      setEditMode(false);
    } else {
      if (projectName !== newName) {
        setSaving(true);
        updateProject(
          { name: event.target.value },
          () => {
            setEditMode(false);
            setSaving(false);
          },
          () => {
            setSaving(false);
            setEditMode(false);
          }
        );
      } else {
        setEditMode(false);
      }
    }
  };

  useEffect(() => {
    if (location.search.includes("focusTitle") && !editMode && projectDate) {
      setTimeout(() => {
        setEditMode(true);
      }, 50);
    }
  }, [location.search, editMode, projectDate]);

  useEffect(() => {
    const handleKeyDown = event => {
      if(productTour.hasActiveTourCurrently() && ["Escape", "Tab"].includes(event.key)) {
        event.preventDefault()
        return
      }
      if (
        event.key === "Escape" &&
        inputRef.current !== null &&
        document.activeElement === inputRef.current
      ) {
        inputRef.current.blur();
      }
      if (
        event.key === "Tab" &&
        inputRef.current !== null &&
        document.activeElement === inputRef.current
      ) {
        event.preventDefault();
        history.replace({
          search: ""
        });
        openDate();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      isProductTourPartProjectInitializeStepSetTitle &&
      !editMode &&
      projectDate
    ) {
      setTimeout(() => {
        setEditMode(true);
      }, 50);
    }
  });

  if (!projectDate) {
    return <Skeleton style={{ backgroundColor: "rgba(0,0,0,.15)" }} />;
  }

  return (
    <div id="project_title_wrapper">
      {!editMode && (
        <OptimizedTooltip
          placement="bottom"
          title={
            productTour.hasActiveTourCurrently() &&
            !isProductTourPartProjectInitializeStepSetTitle
              ? ""
              : projectName && projectName.length > 19
              ? projectName
              : "Rename"
          }
        >
          <Typography
            variant="h5"
            className={classes.title}
            noWrap
            data-cy="projectName-left"
            onClick={onTitleClick}
            style={{
              cursor:
                (productTour.hasActiveTourCurrently() &&
                  !isProductTourPartProjectInitializeStepSetTitle) ||
                isNotAllowedToEdit
                  ? "default"
                  : "pointer",
              pointerEvents:
                (productTour.hasActiveTourCurrently() &&
                  !isProductTourPartProjectInitializeStepSetTitle) ||
                isNotAllowedToEdit
                  ? "none"
                  : "auto"
            }}
          >
            {projectName}
          </Typography>
        </OptimizedTooltip>
      )}
      {editMode && (
        <OutlinedInput
          inputRef={inputRef}
          data-id="project_title_edit"
          defaultValue={projectName}
          classes={{ input: classes.input }}
          onKeyPress={event => {
            if (isProductTourPartProjectInitializeStepBrief) {
              event.preventDefault();
              return;
            }
            if (event.key === "Enter") {
              event.preventDefault();
              event.target.blur();
              if (location.search.includes("focused")) {
                history.replace({
                  search: ""
                });
                if (isProductTourPartProjectInitializeStepSetTitle) {
                  productTour.goToNext();
                }
                openDate();
              }
            }
          }}
          onBlur={onInputBlur}
          disabled={saving}
          autoFocus
          onFocus={event => {
            event.persist();
            setTimeout(() => {
              if (location.search.includes("focusTitle") && projectDate) {
                event.target.select();
                history.replace({
                  search: "focused"
                });
              }
            }, 50);
          }}
          fullWidth
        />
      )}
    </div>
  );
};

export default HeaderTitle;
